import React, { useState, useEffect, useRef } from 'react'
import Modal from '../_layouts/Modal'
import CoachChatList from './CoachChatList'
import useTranslation, {
  TranslationProvider,
} from '../shared/TranslationProvider'
import {
  CandidateChatProvider,
  useCandidateChat,
} from './candidate/CandidateChatContext'
import translations from './locales.json'
import parse from 'html-react-parser'

const CoachChatButtonInner = (props) => {
  const [open, setOpen] = useState(false)
  const [isInFrameset, setIsInFrameset] = useState(false)
  const [mediaAvailable, setMediaAvailable] = useState(true)
  const buttonRef = useRef(null)

  const { chatHours, hideChatHours, candidateChatManager } = useCandidateChat()
  const { t } = useTranslation()

  useEffect(() => {
    // Check if we're inside a frameset
    const checkFrameset = () => {
      const isInFrame = window !== window.top
      const hasFramesetParent = window.parent !== window.top
      const inFrameset = isInFrame || hasFramesetParent
      setIsInFrameset(inFrameset)
    }

    checkFrameset()
  }, [])

  useEffect(() => {
    // Ensure button ref is available after initial render
    if (buttonRef.current) {
      // No-op: just ensure ref is set for future use if needed
    }
  }, [isInFrameset])

  useEffect(() => {
    // Check media availability when component mounts
    const checkMediaAvailability = () => {
      if (candidateChatManager && candidateChatManager.mediaService) {
        const isDegraded = candidateChatManager.mediaService.isDegradedMode()
        setMediaAvailable(!isDegraded)

        if (isDegraded) {
          // console.log(
          //   'CoachChatButton - Media is degraded, hiding video/audio buttons',
          // )
        }
      }
    }

    checkMediaAvailability()
  }, [candidateChatManager])

  const handleOpen = () => {
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
  }

  // Hide the component if it's within a frameset
  if (isInFrameset) {
    return null
  }

  return (
    <div>
      {!open && (
        <button
          ref={buttonRef}
          onClick={handleOpen}
          className={`ga-trackable yns-btn-coach-chat-list btn-opr-coach-chat-button fixed bottom-0 right-8 z-[25000] flex h-16 items-center justify-center rounded-b-none rounded-t-xl bg-intoo-green-dark-900 px-5 text-white hover:bg-intoo-green-dark-800`}
          style={{
            position: 'fixed',
            bottom: '0',
            right: '2rem',
            zIndex: 25000,
          }}
        >
          <div className="flex items-center">
            <div className="inline-flex items-center align-middle">
              <div
                className="relative mr-2 inline-flex h-8 w-8 items-center justify-center align-middle"
                style={{ transformStyle: 'preserve-3d' }}
              >
                <div
                  className="absolute flex h-full w-full items-center justify-center"
                  style={{ backfaceVisibility: 'hidden' }}
                >
                  <img
                    className="max-h-full max-w-full object-contain"
                    alt=""
                    src="https://www-yns-com.s3.amazonaws.com/images/outplacement/icons/coach_button_icon.png"
                  />
                </div>
                <div
                  className="absolute flex h-full w-full items-center justify-center"
                  style={{
                    backfaceVisibility: 'hidden',
                    transform: 'rotateY(180deg)',
                  }}
                >
                  <img
                    className="max-h-full max-w-full object-contain"
                    alt=""
                    src="https://www-yns-com.s3.amazonaws.com/images/outplacement/icons/coach_button_icon.png"
                  />
                </div>
              </div>
            </div>
            <div className="inline-flex items-center align-middle">
              {t('CoachChatButton.button_text')}
            </div>
          </div>
        </button>
      )}

      <Modal
        open={open}
        onClose={handleClose}
        title={t('CoachChatButton.modal_title')}
        icon="coach-services"
        size="md"
        variant="secondary"
        fullHeight={true}
        closeButtonLabel={t('CoachChatButton.close_button')}
      >
        <CoachChatList {...props} open={open} mediaAvailable={mediaAvailable} />
        {/* CTS-9779: three-zone footer — hours | instruction | article link */}
        <div className="grid w-full grid-cols-1 items-start gap-4 text-sm leading-5 text-cod-gray-700 md:grid-cols-3 md:gap-6">
          <div className="flex flex-col gap-1 text-left">
            {!hideChatHours && (
              <>
                <div>{t('CoachChatButton.chat_is_available_colon')}</div>
                <div>{parse(chatHours)}</div>
              </>
            )}
          </div>
          <div className="text-center text-sm leading-5 text-cod-gray-700">
            {t('CoachChatButton.select_coach_instruction')}
          </div>
          <div className="text-left md:text-right">
            <a
              className="text-sm leading-5 text-intoo-green-medium-800 underline hover:text-intoo-green-medium-900"
              href="/coach_services/article/ways-to-utilize-coach-chat"
            >
              {t('CoachChatButton.learn_coach_chat_link')}
            </a>
          </div>
        </div>
      </Modal>
    </div>
  )
}

const CoachChatButton = (props) => {
  return (
    <TranslationProvider
      locale={props.current_user.locale}
      translations={translations}
      scope="javascript.components.coach_chat"
    >
      <CandidateChatProvider {...props}>
        <CoachChatButtonInner {...props} />
      </CandidateChatProvider>
    </TranslationProvider>
  )
}

export default CoachChatButton
